/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of AppEventReportor
 */

import { ITrackerContext } from 'com/didichuxing/tracker/context/ITrackerContext';
import { IEventReportor } from 'com/didichuxing/tracker/plugin/reportor/IEventReportor';
import { IOmegaSDK } from 'com/didichuxing/tracker/plugin/app/IOmegaSDK';
import { IAttrs } from 'com/didichuxing/tracker/IAttrs';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { ITrackedEventData } from 'com/didichuxing/tracker/event/ITrackedEventData';
import AEvent from 'com/didichuxing/tracker/plugin/events/AEvent';
import FailedEvent from 'com/didichuxing/tracker/plugin/events/FailedEvent';
import TimeoutEvent from 'com/didichuxing/tracker/plugin/events/TimeoutEvent';
import AppUtil from 'com/didichuxing/tracker/plugin/utils/AppUtil';
import GlobalUtil from 'com/didichuxing/tracker/plugin/utils/GlobalUtil';

/**
 * 上报超时默认时间，单位毫秒。
 * @type {number}
 */
const REPORT_TIMEOUT: number = 3000;

export default class AppEventReportor implements IEventReportor {

    private context: ITrackerContext;
    private event: ITrackedEvent;

    /**
     * @constructor
     * @param {ITrackerContext} context
     * @param {ITrackedEvent} event
     */
    constructor(context: ITrackerContext, event: ITrackedEvent) {
        this.context = context;
        this.event = event;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {Promise<ITrackedEvent>}
     */
    public reportEvent(): Promise<ITrackedEvent> {
        return new Promise<ITrackedEvent>((resolve: (evt: ITrackedEvent) => void,
                                           reject: (evt: ITrackedEvent) => void): void => {
            if (!GlobalUtil.isEmptyString(this.event.getData().appKey || '')) {
                GlobalUtil.dispatchOmgLog('[Omega]na appKey 参数 不可为空');
                throw new Error('[Omega]na appKey 参数 不可为空');
                reject(new FailedEvent(new Error('[Omega]na appKey 参数 不可为空'),
                    this.event.getType(), this.event.getData().attrs || {}, this.context));
            }
            const omegaSDK = AppUtil.getOmegaSDK();
            if (omegaSDK) {
                const timeoutHandler: number = setTimeout((): void => {
                    clearTimeout(timeoutHandler);
                    reject(new TimeoutEvent(this.event.getType(), this.event.getData().attrs || {}, this.context));
                }, this.context.timeout || REPORT_TIMEOUT) as any;
                try {
                    const eventData: ITrackedEventData = this.event.getData();
                    if (eventData.userId && omegaSDK.setUserId) {
                        omegaSDK.setUserId(eventData.userId);
                    }
                    if (eventData.telephone && omegaSDK.setTelephone) {
                        omegaSDK.setTelephone(eventData.telephone);
                    }
                    if (eventData.cityId && omegaSDK.setCityId) {
                        omegaSDK.setCityId(Number(eventData.cityId));
                    }
                    const attrs: IAttrs = eventData.attrs || {};
                    omegaSDK.trackEventEx(eventData.eventId, attrs.label || '',
                        (this.event as AEvent).serializeAttrs2JsonStr(attrs),
                        (this.event as AEvent).serialize2JsonStrWithoutAttrs(), '{nt:1}');
                    clearTimeout(timeoutHandler);
                    resolve(this.event);
                } catch (error) {
                    GlobalUtil.dispatchOmgLog('端上数据发送失败' + this.event.getType());
                    clearTimeout(timeoutHandler);
                    reject(new FailedEvent(error, this.event.getType(),
                        this.event.getData().attrs || {}, this.context));
                }
            } else {
                GlobalUtil.dispatchOmgLog('na 未能找到omegaSDK');
                reject(new FailedEvent(new Error('[Omega]Cannot fetch the omegaSDK from the native omega sdk.'),
                    this.event.getType(), this.event.getData().attrs || {}, this.context));
            }
        });
    }
}
